import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner } from '../ui.jsx';

export function Timesheets({ toast }) {
  const [timesheets, setTimesheets] = useState([]);
  const [techs, setTechs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockIn, setClockIn] = useState(null);
  const [techFilter, setTechFilter] = useState('all');

  const load = () => Promise.all([api('/api/timesheets'), api('/api/techs'), api('/api/jobs')])
    .then(([ts,t,j]) => { setTimesheets(ts); setTechs(t); setJobs(j); })
    .catch(e=>toast(e.message,'error'))
    .finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = timesheets.filter(t => techFilter==='all' || t.techId === techFilter);
  const open = filtered.filter(t => !t.clockedOutAt);
  const total = filtered.reduce((s,t)=>s+(Number(t.hours)||0),0);
  const totalWage = filtered.reduce((s,t)=>s+t.wage,0);

  const clockOut = async id => {
    try { await api(`/api/timesheets/${id}/clock-out`, { method:'POST' }); load(); toast('Clocked out'); }
    catch(e) { toast(e.message,'error'); }
  };
  const remove = async id => {
    if (!confirm('Delete this entry?')) return;
    try { await api(`/api/timesheets/${id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Timesheets & Payroll</h1>
      <Btn v="primary" onClick={()=>setClockIn({ techId:'', jobId:'', type:'job', notes:'' })}>+ Clock In</Btn>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:20 }}>
      <Stat label="Active Now" value={open.length} sub="techs clocked in" color={G.green}/>
      <Stat label="Total Hours" value={total.toFixed(1)} sub="this period" color={G.cyan}/>
      <Stat label="Total Wages" value={fmtUsd(totalWage)} sub="hourly cost" color={G.purple}/>
      <Stat label="Avg Rate" value={fmtUsd(total>0?totalWage/total:0)+'/hr'} sub="effective rate" color={G.orange}/>
    </div>

    <div style={{ display:'flex', gap:10, marginBottom:14 }}>
      <Select value={techFilter} onChange={e=>setTechFilter(e.target.value)} style={{ maxWidth:240 }}>
        <option value="all">All Techs</option>
        {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </Select>
    </div>

    {open.length > 0 && <div style={{ ...styles.card, marginBottom:14, borderColor: G.green+'66' }}>
      <div style={{ fontSize:13, fontWeight:700, color:G.green, marginBottom:10, textTransform:'uppercase', letterSpacing:'.05em' }}>🟢 Currently Clocked In</div>
      {open.map(t => (
        <div key={t.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${G.border}` }}>
          <div>
            <strong>{t.techName}</strong>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{t.jobName||t.type} · started {new Date(t.clockedInAt).toLocaleTimeString()}</div>
          </div>
          <Btn v="green" sz="sm" onClick={()=>clockOut(t.id)}>Clock Out</Btn>
        </div>
      ))}
    </div>}

    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Tech','Type','Job','Clock In','Clock Out','Hours','Rate','Wage',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{filtered.filter(t=>t.clockedOutAt).slice(0,200).map(t => (
          <tr key={t.id}>
            <td style={{ ...styles.td, fontWeight:600 }}>{t.techName}</td>
            <td style={styles.td}><Badge c={t.type==='job'?G.green:G.textMuted} bg={(t.type==='job'?G.green:G.textMuted)+'22'}>{t.type}</Badge></td>
            <td style={styles.td}>{t.jobName||'—'}</td>
            <td style={{ ...styles.td, fontSize:12 }}>{new Date(t.clockedInAt).toLocaleString()}</td>
            <td style={{ ...styles.td, fontSize:12 }}>{new Date(t.clockedOutAt).toLocaleString()}</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{Number(t.hours||0).toFixed(2)}</td>
            <td style={styles.td}>{fmtUsd(t.hourlyRate)}/hr</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(t.wage)}</td>
            <td style={styles.td}><Btn sz="xs" v="danger" onClick={()=>remove(t.id)}>×</Btn></td>
          </tr>
        ))}</tbody>
      </table>
      {filtered.length === 0 && <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>No timesheet entries yet. Click "Clock In" to start one.</div>}
    </div>

    <ClockInModal data={clockIn} techs={techs} jobs={jobs} onClose={()=>setClockIn(null)} onSaved={()=>{ load(); setClockIn(null); toast('Clocked in'); }}/>
  </div>;
}

function Stat({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor: color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}

function ClockInModal({ data, techs, jobs, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(data||{}); }, [data]);
  if (!data) return null;
  const submit = async () => {
    if (!form.techId) return alert('Select a tech');
    try { await api('/api/timesheets/clock-in', { method:'POST', body: form }); onSaved(); }
    catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={460}>
    <h2 style={{ margin:'0 0 14px 0' }}>Clock In</h2>
    <Field label="Tech *">
      <Select value={form.techId||''} onChange={e=>setForm({...form,techId:e.target.value})}>
        <option value="">— select —</option>
        {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </Select>
    </Field>
    <Field label="Type">
      <Select value={form.type||'job'} onChange={e=>setForm({...form,type:e.target.value})}>
        <option value="job">On Job</option>
        <option value="shop">Shop / Travel</option>
        <option value="admin">Admin</option>
      </Select>
    </Field>
    {form.type === 'job' && <Field label="Job">
      <Select value={form.jobId||''} onChange={e=>setForm({...form,jobId:e.target.value})}>
        <option value="">— select —</option>
        {jobs.filter(j => !['Completed','Cancelled'].includes(j.status)).map(j => <option key={j.id} value={j.id}>{j.name} ({j.contact})</option>)}
      </Select>
    </Field>}
    <Field label="Notes (optional)"><Input value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})}/></Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit}>Clock In</Btn>
    </div>
  </Modal>;
}
